import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CtaLink } from "@/src/common/components/cta-link";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { SITE_ROUTES } from "@/src/common/constants/site-routes";
import { getStringArray } from "@/src/common/utils/i18n.utils";

interface TeaserTier {
	id: "free" | "pro" | "premium";
	highlight?: boolean;
}

const TIERS: ReadonlyArray<TeaserTier> = [{ id: "free" }, { id: "pro", highlight: true }, { id: "premium" }];

export function PricingTeaser() {
	const { t } = useTranslation("home");

	return (
		<section className="py-24 md:py-32">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<SectionHeading
					title={t("pricing_teaser.title")}
					titleAccent={t("pricing_teaser.title_accent")}
					description={t("pricing_teaser.subtitle")}
				/>

				<div className="mt-16 grid gap-6 md:grid-cols-3">
					{TIERS.map((tier, index) => {
						const base = `pricing_teaser.${tier.id}`;
						const isPremium = tier.id === "premium";
						const features = getStringArray(t(`${base}.features`, { returnObjects: true }));
						const card = (
							<article
								className={`relative flex h-full flex-col gap-5 rounded-3xl p-8 shadow-card hover-lift ${
									tier.highlight
										? "bg-white"
										: isPremium
											? "border border-transparent bg-gradient-to-b from-brand-900 to-brand-800 text-white"
											: "border border-default-200 bg-white"
								}`}
							>
								<header className="flex flex-col gap-2">
									<p className={`text-sm font-bold uppercase tracking-wider ${isPremium ? "text-white/80" : "text-brand-700"}`}>
										{t(`${base}.name`)}
									</p>
									<div className="flex items-baseline gap-2">
										<span className={`text-4xl font-bold ${isPremium ? "text-white" : "text-foreground"}`}>{t(`${base}.price`)}</span>
										{tier.id === "pro" ? <span className="text-sm text-default-500">{t("pricing_teaser.pro.period")}</span> : null}
									</div>
									<p className={`text-sm ${isPremium ? "text-white/70" : "text-default-600"}`}>{t(`${base}.body`)}</p>
								</header>

								<CtaLink
									to={SITE_ROUTES.PRICING + (tier.id === "premium" ? "#premium" : "")}
									variant={tier.highlight ? "gradient" : isPremium ? "white" : "outline"}
									size="md"
									className="w-full"
								>
									{t(`${base}.cta`)}
								</CtaLink>

								<ul className={`mt-2 space-y-2 text-sm ${isPremium ? "text-white/80" : "text-default-700"}`}>
									{features.map((feat) => (
										<li key={feat} className="flex items-start gap-2">
											<Check className={`mt-0.5 h-4 w-4 shrink-0 ${isPremium ? "text-white/80" : "text-brand-600"}`} />
											<span>{feat}</span>
										</li>
									))}
								</ul>
							</article>
						);

						return (
							<Reveal key={tier.id} delay={index * 0.1} className="relative">
								{tier.highlight ? (
									<>
										<div className="conic-border h-full">{card}</div>
										<span className="absolute -top-3 left-1/2 z-10 inline-flex -translate-x-1/2 items-center rounded-full bg-brand-gradient px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow">
											{t("pricing_teaser.pro.ribbon")}
										</span>
									</>
								) : (
									card
								)}
							</Reveal>
						);
					})}
				</div>

				<Reveal delay={0.3} className="mt-10 text-center">
					<CtaLink to={SITE_ROUTES.PRICING} variant="outline" size="md" endContent={<ArrowRight className="h-4 w-4" />}>
						{t("pricing_teaser.see_all")}
					</CtaLink>
				</Reveal>
			</div>
		</section>
	);
}
